/** Collapses the historical option shapes into `{ id, value }` and a `correctAnswer` that always holds ids. */
export interface EditorOption {
  id: string;
  value: string;
}

export type EditorCorrectAnswer = string | string[] | Record<string, string>;

/** Localize standard boolean labels at display time, keeping stored answers and IDs stable. */
export function getQuestionOptionLabel(
  type: string,
  value: string,
  translate: (key: 'option_true' | 'option_false') => string,
): string {
  if (type !== 'TRUE_FALSE') return value;
  const label = value.trim().toLowerCase().replace(/[‘’ʻʼ]/g, "'");
  if (['true', "to'g'ri", 'верно'].includes(label)) return translate('option_true');
  if (['false', "noto'g'ri", 'неверно'].includes(label)) return translate('option_false');
  // Custom authored labels are content; never guess their meaning from order or the answer key.
  return value;
}

/** Stable id for a bare-string option so True/False keep matching a value-based answer. */
const stringOptionId = (raw: string): string => {
  const lower = raw.trim().toLowerCase();
  if (lower === 'true') return 'true';
  if (lower === 'false') return 'false';
  return raw;
};

const normalizeOption = (raw: unknown, index: number): EditorOption => {
  if (typeof raw === 'string') return { id: stringOptionId(raw), value: raw };
  const obj = (raw ?? {}) as Record<string, unknown>;
  const value = (obj.value ?? obj.text ?? '') as string;
  // Fall back to a deterministic positional id so options that arrive without
  // one (legacy / AI-generated) still bind stably to a value-based answer.
  return { id: (obj.id as string) || `opt_${index}`, value };
};

/** The returned `correctAnswer` always references the returned options' ids. */
export function normalizeQuestionAnswers(
  rawOptions: unknown,
  rawCorrectAnswer: unknown,
): { options: EditorOption[]; correctAnswer: EditorCorrectAnswer } {
  const options: EditorOption[] = Array.isArray(rawOptions)
    ? rawOptions.map(normalizeOption)
    : [];

  // Keep a value already equal to an option id; otherwise map value → id.
  const toId = (answer: unknown): string => {
    // A missing key must never select an unfinished option with an empty value.
    if (answer === '') return '';
    if (options.some((o) => o.id === answer)) return answer as string;
    const match = options.find((o) => o.value === answer);
    return match ? match.id : (answer as string);
  };

  const raw = rawCorrectAnswer ?? '';
  let correctAnswer: EditorCorrectAnswer;
  if (Array.isArray(raw)) {
    correctAnswer = raw.map(toId);
  } else if (raw && typeof raw === 'object') {
    // Matching answers are id→id maps, not option references.
    correctAnswer = raw as Record<string, string>;
  } else {
    correctAnswer = toId(raw);
  }

  return { options, correctAnswer };
}
